import {
  BubbleCell,
  CustomCell,
  DrawArgs,
  GridCellKind,
  ProvideEditorCallback,
  type CustomRenderer,
} from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ThemeMode } from '@makyoapp/makyo-ui'

import { ArrayCell } from '@/core/models/columns/array'

import { drawBubbles } from '../helpers/draw-fns'
import { getEditorStyle } from '../helpers/editor-style-helper'
import { ArrayEditor } from './ArrayEditor'

interface CustomArrayCellProps {
  readonly type: ColumnTypes
  readonly cell: ArrayCell
  readonly extra: { theme: ThemeMode }
  readonly id: string
}

export type CustomArrayCell = CustomCell<CustomArrayCellProps>

const Editor: ReturnType<ProvideEditorCallback<CustomArrayCell>> = ({ value, onFinishedEditing }) => {
  const cell = value.data.cell as ArrayCell

  return (
    <ArrayEditor
      id={value.data.id}
      value={value.data.cell.value?.toString().split(',')}
      onChange={(nextValue) => {
        if (nextValue && Array.isArray(nextValue)) {
          let updatedCell = cell.toCell(nextValue) as ArrayCell

          onFinishedEditing({
            ...value,
            data: {
              ...value.data,
              cell: updatedCell,
            },
          })
        } else {
          onFinishedEditing()
        }
      }}
    />
  )
}

const CustomArrayRenderer: CustomRenderer<CustomArrayCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomArrayCell =>
    (cell.data as CustomArrayCellProps).type === ColumnTypes.Array,
  needsHover: false,
  needsHoverPosition: false,
  measure: (ctx, cell, t) => {
    const cellValue = cell.data.cell.toString().trim()
    return (
      cellValue.split(',').reduce((acc, data) => ctx.measureText(data).width + acc + 20, 0) +
      2 * t.cellHorizontalPadding -
      4
    )
  },
  draw: async (args, cell) => {
    const cellValue = cell.data.cell.toString().trim()
    if (!cellValue) {
      return false
    }
    drawBubbles(args as unknown as DrawArgs<BubbleCell>, cellValue.split(','), cell.data.extra.theme)
  },
  provideEditor: () => {
    const style = { width: '600px', overflow: 'hidden' }
    const customEditorHeight = getEditorStyle(210, true)

    return {
      disablePadding: true,
      disableStyling: false,
      styleOverride: { ...style, ...customEditorHeight },
      editor: Editor,
    }
  },
  onPaste: (toPaste, cellData) => {
    toPaste = toPaste.trim()
    const fragments = toPaste.split(',')
    const texts = fragments.filter((x) => x !== undefined && x !== '') as string[]
    const cellValue = cellData.cell.toString()
    if (
      Array.isArray(cellData.cell.value) &&
      texts.length === cellValue.length &&
      texts.every((u, i) => (cellData.cell.value as string[])[i] && u === (cellData.cell.value as string[])[i])
    )
      return undefined
    return {
      ...cellData,
      data: texts,
    }
  },
}

export default CustomArrayRenderer
